import type {
  AnalyticsAPI,
  AnalyticsManagerConfig,
  AnalyticsPayload,
  GlobalProperties,
  UserIdentificationPayload,
} from './analytics.type';
import { createMixPanelAPI } from './mixpanelAPI';

export const createAnalyticsManager = <
  Events extends Record<string, unknown>,
>() => {
  let shouldIgnoreUser = false;
  let mixpanelAPI: Nullable<AnalyticsAPI> = null;

  const identifyUser = async (userProperties: UserIdentificationPayload) => {
    if (mixpanelAPI) {
      mixpanelAPI.identifyUser(userProperties);
    }
  };

  const initialize = async (config: AnalyticsManagerConfig) => {
    if (config.shouldIgnoreUser) {
      shouldIgnoreUser = config.shouldIgnoreUser;
      return;
    }
    const mixpanelToken = config.mixpanelToken;
    const mixpanelApiHost = config.mixpanelApiHost;
    const debugMode = config.debugMode ?? false;
    const initializedMixpanelAPI = await createMixPanelAPI(
      mixpanelToken,
      mixpanelApiHost,
      debugMode
    );
    mixpanelAPI = initializedMixpanelAPI;
    if (config.globalProperties)
      initializedMixpanelAPI.registerGlobalProperties(config.globalProperties);
    const userProperties = config.userProperties;
    if (!userProperties) return;
    await identifyUser(userProperties);
  };

  const updateUserProperties = async (
    userProperties: UserIdentificationPayload
  ) => {
    if (!mixpanelAPI || shouldIgnoreUser) return;
    await identifyUser(userProperties);
  };

  const setGroup = (groupId: string, value: string | number) => {
    if (!mixpanelAPI || shouldIgnoreUser) return;
    mixpanelAPI.setGroup(groupId, value);
  };

  const getGroup = (groupId: string, value: string) => {
    if (!mixpanelAPI || shouldIgnoreUser) return;
    return mixpanelAPI.getGroup(groupId, value);
  };

  const emit = async <K extends keyof Events>(
    event: K & string,
    payload?: Events[K]
  ) => {
    if (!mixpanelAPI || shouldIgnoreUser) return;
    mixpanelAPI.handleEvent(event, payload as AnalyticsPayload);
  };

  const registerGlobalProperties = (properties: GlobalProperties) => {
    if (!mixpanelAPI || shouldIgnoreUser) return;
    mixpanelAPI.registerGlobalProperties(properties);
  };

  return {
    initialize,
    updateUserProperties,
    emit,
    identifyUser,
    registerGlobalProperties,
    setGroup,
    getGroup,
  };
};
